import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';
import { motion } from 'framer-motion';

export const BlogPostPage = () => {
  const { slug } = useParams();
  const { t, getLocalizedText, API } = useApp();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/blog/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setPost(data);
        }
      } catch (err) {
        console.error('Failed to fetch blog post', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, API]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
            <div className="h-10 bg-gray-200 rounded mb-6" />
            <div className="aspect-[16/9] bg-gray-200 rounded-lg mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-4 py-8" data-testid="blog-post-page">
        <Link
          to="/blog"
          className="inline-flex items-center text-muted-foreground hover:text-primary mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(post.created_at)}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1625] mb-6">
            {getLocalizedText(post.title)}
          </h1>

          {post.featured_image && (
            <div className="aspect-[16/9] overflow-hidden rounded-lg mb-8">
              <img
                src={post.featured_image}
                alt={getLocalizedText(post.title)}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div
            className="prose prose-lg max-w-none text-foreground prose-headings:font-heading prose-headings:text-[#1A1625] prose-a:text-primary prose-p:whitespace-pre-line"
            dangerouslySetInnerHTML={{ 
              __html: getLocalizedText(post.content)
                .replace(/\n\n/g, '</p><p>')
                .replace(/\n/g, '<br/>')
                .replace(/^(.*)$/, '<p>$1</p>')
            }}
          />

          {post.tags?.length > 0 && (
            <div className="flex items-center gap-2 mt-8 pt-8 border-t">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="text-sm bg-purple-50 text-primary px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </article>
    </Layout>
  );
};

export default BlogPostPage;
